import React, { useEffect, useState } from "react";
import "./AgencyProfileVerifyPhone.scss";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import { toast } from "react-hot-toast";
import {
  AuthErrorCodes,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { FirebaseServices } from "../../../services/firebase.services";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import { CommonHelpers } from "../../../utils/helpers/common.helpers";
import { CommonEnums } from "../../../enums/common.enums";
import { AuthApi } from "../../../utils/api/Auth";
import { clientRoutes } from "../../../utils/routes";
import { AgencyApi } from "../../../utils/api/Agency";
import AgencyProfileSteps from "./AgencyProfileSteps";
import AgentCreatingAgencySteps from "./AgencyProfileSteps";

const AgentAgencyProfileVerifyPhone = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const openLogoutModal = () => setIsLogoutModalOpen(true);
  const closeLogoutModal = () => setIsLogoutModalOpen(false);
  const [agentRegistrationPopup, setAgentRegistrationPopup] = useState({
    open: false,
  });

  const [state, setState] = useState<any>({
    isLoading: false,
    isLoadingResend: false,
    verificationCodeInputError: false,
    editPhone: false,
    phoneNo: "",
    phoneCode: "+61",
    isPhoneVerified: false,
    verificationCode: "",
    updateIsLoading: false,
  });

  useEffect(() => {
    if (!document.getElementById("agency-verify-phone-submit-btn")) return;

    if (!(window as any).recaptchaVerifier) {
      (window as any).recaptchaVerifier = new RecaptchaVerifier(
        FirebaseServices.firebaseAuth,
        "agency-verify-phone-submit-btn",
        {
          size: "invisible",
          callback: (_response: any) => {
            console.log("Recaptcha Verified");
          },
          "expired-callback": () => {
            console.log("Recaptcha Expired");
          },
        },
      );
    }

    let phoneCode = "";
    let phone = "";
    let registrationData: any = null;

    registrationData = LocalStorageHelpers.getAgencyData();
    phone = registrationData?.principal_agent_mobile_number as string;
    phoneCode = registrationData?.phone_country_code as string;

    if (!phoneCode?.includes("+") && phoneCode) {
      phoneCode = "+" + phoneCode?.trim();
    } else if (!phoneCode) {
      phoneCode = "+61";
    }

    setState((prev: any) => ({
      ...prev,
      phoneNo: phone?.trim() || "",
      phoneCode,
      isPhoneVerified: registrationData?.mobile_verification ?? false,
    }));

    return () => {
      if ((window as any).recaptchaVerifier) {
        (window as any).recaptchaVerifier.clear();
        (window as any).recaptchaVerifier = null;
      }
    };
  }, []);

  const resendOtp = async (params: { phone: string }) => {
    if (!(window as any).recaptchaVerifier) {
      return false;
    }

    const appVerifier = (window as any).recaptchaVerifier;
    const auth = FirebaseServices.firebaseAuth;

    try {
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        params.phone,
        appVerifier,
      );

      FirebaseServices.setPhoneConfirmationResult(confirmationResult);
      return true;
    } catch (err: any) {
      console.log("sendOtp >>> error >>>", err);
      if (err.code === "auth/too-many-requests") {
        toast.error(
          "Too many attempts. Please wait a while or use a test number.",
        );
      } else {
        toast.error(err.message || "Failed to send verification code.");
      }
      return false;
    }
  };

  const handleContinue = async () => {
    const agencyData = LocalStorageHelpers.getAgencyData();

    if (agencyData?._id) {
      const fd = new FormData();
      fd.append("registration_status", "pending_for_legal_info");
      fd.append("automatic_timezone", "true");

      await AgencyApi.updateAgencyProfile(fd, agencyData._id);
    }

    navigate(clientRoutes.agentCreateAgencyLegalInfo);
  };

  const verifyOtp = async () => {
    try {
      setState((prev: any) => ({ ...prev, isLoading: true }));

      const code = state.verificationCode;

      if (!code || code?.length < 5) {
        setState((prev: any) => ({
          ...prev,
          isLoading: false,
          verificationCodeInputError: true,
        }));
        return;
      }

      const confirmationResult = FirebaseServices.getPhoneConfirmationResult();

      if (!confirmationResult) {
        toast.error("Session expired or invalid. Please resend code.");
        setState((prev: any) => ({ ...prev, isLoading: false }));
        return;
      }

      await confirmationResult.confirm(code);
      FirebaseServices.removeConfirmationResult();

      const isAgencyRoute = CommonHelpers.isAgencyRoute({ path: pathname });
      let userType = CommonEnums.roles.agent;
      if (isAgencyRoute) userType = CommonEnums.roles.agency;

      const verificationRes = await AuthApi.verifyPhone({
        user_type: userType,
      });

      if (!verificationRes.success) {
        toast.error(verificationRes.data?.message || "Verification failed");
        setState((prev: any) => ({
          ...prev,
          isLoading: false,
          verificationCodeInputError: false,
        }));
        return;
      }

      const storedAgency = LocalStorageHelpers.getAgencyData();
      const agencyId =
        storedAgency?._id || verificationRes?.data?.data?.user_data?._id;

      if (!agencyId) {
        toast.error("Agency not found after verification. Please login again.");
        setState((prev: any) => ({ ...prev, isLoading: false }));
        return;
      }

      const freshAgencyRes = await AgencyApi.getAgencyById({
        agency_id: agencyId,
      });

      if (!freshAgencyRes.success) {
        toast.error("Failed to load updated agency data.");
        setState((prev: any) => ({ ...prev, isLoading: false }));
        return;
      }

      const freshAgencyData = freshAgencyRes.data?.data;

      try {
        const agencyIdToUpdate = freshAgencyData?._id || agencyId;

        if (agencyIdToUpdate) {
          const fd = new FormData();
          fd.append("registration_status", "pending_for_legal_info");
          fd.append("automatic_timezone", "true");

          await AgencyApi.updateAgencyProfile(fd, agencyIdToUpdate);

          const updatedAgencyRes = await AgencyApi.getAgencyById({
            agency_id: agencyIdToUpdate,
          });

          if (updatedAgencyRes?.data?.success) {
            const updatedAgencyData = updatedAgencyRes.data.data;
            LocalStorageHelpers.setAgencyData({ data: updatedAgencyData });
            LocalStorageHelpers.setSessionUserType({
              type: updatedAgencyData?.role,
            });
          }
        }
      } catch (e) {
        console.error("Failed to update registration status", e);
      }

      LocalStorageHelpers.setAgencyData({ data: freshAgencyData });
      LocalStorageHelpers.setSessionUserType({ type: freshAgencyData?.role });

      toast.success("Your phone is verified successfully");

      setState((prev: any) => ({ ...prev, isLoading: false }));

      navigate(clientRoutes.agentCreateAgencyLegalInfo);
    } catch (err: any) {
      console.log("error", err);
      const errorCode = err.code;
      if (errorCode === AuthErrorCodes.CODE_EXPIRED)
        toast.error("Verification code is expired, please resend!");
      else if (errorCode === AuthErrorCodes.INVALID_CODE)
        toast.error("Verification code is invalid!");
      else toast.error("Something went wrong during verification.");

      setState((prev: any) => ({ ...prev, isLoading: false }));
    }
  };

  const onClickResendOtp = async () => {
    const phoneNumber = `${state?.phoneCode}${state?.phoneNo}`;

    setState((prev: any) => ({
      ...prev,
      isLoadingResend: true,
      isLoading: false,
      verificationCodeInputError: false,
    }));

    const phoneAuthRes = await resendOtp({
      phone: phoneNumber,
    });

    if (phoneAuthRes) {
      toast.success("Verification code sent successfully");
    }

    setState((prev: any) => ({
      ...prev,
      isLoadingResend: false,
    }));
  };

  const updateMobileNumber = async () => {
    let agencyData = LocalStorageHelpers.getAgencyData();

    setState((prev: any) => ({ ...prev, updateIsLoading: true }));

    let formData = new FormData();
    formData.append("principal_agent_mobile_number", state?.phoneNo);
    const cleanCode = state?.phoneCode.replace("+", "");
    formData.append("phone_country_code", cleanCode);

    formData.append("automatic_timezone", "true");

    let res = await AgencyApi.updateAgencyProfile(
      formData,
      agencyData?._id || "",
    );

    if (res?.data?.success) {
      const phoneNumber = `${state?.phoneCode}${state?.phoneNo}`;

      const phoneAuthRes = await resendOtp({ phone: phoneNumber });

      if (phoneAuthRes) {
        toast.success("Number updated & verification code sent");
        setState((prev: any) => ({
          ...prev,
          editPhone: false,
          updateIsLoading: false,
          verificationCode: "",
        }));
      } else {
        setState((prev: any) => ({ ...prev, updateIsLoading: false }));
      }
    } else {
      setState((prev: any) => ({ ...prev, updateIsLoading: false }));
      toast.error(res?.data?.message || "Failed to update phone number.");
    }
  };

  const updatePhoneCode = (e: any) => {
    setState((prev: any) => ({ ...prev, phoneCode: e.target.value }));
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev: any) => ({
      ...prev,
      verificationCode: e.target.value,
      verificationCodeInputError: false,
    }));
  };

  const handleBack = () => {
    navigate(clientRoutes.agentCreateAgencyInfo);
  };

  const onClickEdit = () => {
    setState((prev: any) => ({ ...prev, editPhone: true }));
  };

  const onCancelEdit = () => {
    let registrationData = LocalStorageHelpers.getAgencyData();
    let phone = registrationData?.principal_agent_mobile_number as string;

    setState((prev: any) => ({
      ...prev,
      editPhone: false,
      phoneNo: phone,
    }));
  };

  const onEditAlreadyVerified = () => {
    setState((prev: any) => ({
      ...prev,
      isPhoneVerified: false,
      editPhone: true,
    }));
  };

  const handleLogout = () => {
    // LocalStorageHelpers.clearStorage();
    LocalStorageHelpers.clear();
    navigate(clientRoutes.login);
  };

  const handleLogoutConfirm = () => {
    closeLogoutModal();
    LocalStorageHelpers.clear();
    navigate(clientRoutes.login);
  };

  const confirmAgentRegistration = () => {
    const registrationData = LocalStorageHelpers.getRegistrationData();
    let registerLink = `${clientRoutes.register}?${CommonEnums.queryParams.register_as}=${CommonEnums.roles.agent}&${CommonEnums.queryParams.agent_email}=${registrationData?.email}`;
    navigate(registerLink);
  };

  const LogoutModal = (
    <Modal
      open={isLogoutModalOpen}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") closeLogoutModal();
        else closeLogoutModal();
      }}
      className="logout-confirm-modal"
    >
      <div className="logout-modal-center">
        <div className="logout-modal-card">
          <h3 className="logout-modal-title">Log out?</h3>

          <p className="logout-modal-subtitle">
            Unsaved changes will be discarded.
            <br />
            Are you sure you want to proceed?
          </p>

          <div className="logout-modal-actions">
            <button className="logout-cancel-btn" onClick={closeLogoutModal}>
              Cancel
            </button>

            <button
              className="logout-confirm-btn"
              onClick={handleLogoutConfirm}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );

  const CommonFooter = () => (
    <div className="common-footer">
      <div className="terms-text">
        By continuing, you agree to our{" "}
        <span className="link">Terms and Conditions</span>
        <br />
        Read our{" "}
        <span className="link">Personal Information Collection Statement</span>
      </div>
      <div className="save-progress-text">
        Profile progress saved.
        <br />
        Feel free to log out; you can continue your setup at any time.
        <br />
        <span className="link logout" onClick={openLogoutModal}>
          Logout
        </span>
      </div>
    </div>
  );

  if (state.isPhoneVerified) {
    return (
      <>
        <button id="agency-verify-phone-submit-btn" style={{ display: "none" }}>
          Send Otp
        </button>
        <div className="steps-container">
          <AgentCreatingAgencySteps
            step={CommonEnums.agencyProfileSteps.verifyPhone}
            agencyData={state.agencyData}
          />
        </div>
        <div className="agency-verify-phone-container verified-view">
          <div className="content-wrapper">
            <div className="success-icon-box">
              <img
                src="/images/trueiconverifynumber.svg"
                alt="Verified"
                className="verify-icon-img"
              />
            </div>
            <h2>Phone Already Verified</h2>
            <p className="sub-text verified-sub-text">
              {/* You're all set. Let's find your agency to complete your setup. */}
              Let's continue with your agency's legal information
            </p>

            <div className="edit-link-container">
              {/* <span onClick={onEditAlreadyVerified} className="link-style">
                Edit phone number
              </span> */}
            </div>

            <div className="button-row">
              <button className="btn-back-outline" onClick={handleBack}>
                Back
              </button>
              <button className="btn-filled" onClick={handleContinue}>
                Continue
              </button>
            </div>
            <CommonFooter />
            {LogoutModal}
          </div>
        </div>
      </>
    );
  }

  if (state.editPhone) {
    return (
      <>
        <button id="agency-verify-phone-submit-btn" style={{ display: "none" }}>
          Send Otp
        </button>
        <div className="steps-container">
          <AgentCreatingAgencySteps
            step={CommonEnums.agencyProfileSteps.verifyPhone}
            agencyData={state.agencyData}
          />
        </div>
        <div className="agency-verify-phone-container edit-view">
          <div className="content-wrapper">
            <h2>Edit Work Mobile</h2>
            <p className="sub-text">
              Update your number below to receive a new verification code.
            </p>

            <div className="input-group-label">Work Mobile*</div>
            <div className="mobile-input-row">
              <Select
                value={state?.phoneCode}
                onChange={updatePhoneCode}
                className="country-select"
                variant="standard"
                disableUnderline
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value="+61">+61</MenuItem>
                <MenuItem value="+91">+91</MenuItem>
                <MenuItem value="+64">+64</MenuItem>
                <MenuItem value="+1">+1</MenuItem>
                <MenuItem value="+44">+44</MenuItem>
              </Select>
              <TextField
                variant="standard"
                placeholder="1234567890"
                value={state?.phoneNo}
                onChange={(e) =>
                  setState((prev: any) => ({
                    ...prev,
                    phoneNo: e?.target?.value,
                  }))
                }
                className="number-input"
                InputProps={{ disableUnderline: true }}
              />
            </div>

            <div className="button-row edit-button">
              <button className="btn-back-outline" onClick={onCancelEdit}>
                Cancel
              </button>
              <button className="btn-filled" onClick={updateMobileNumber}>
                {state?.updateIsLoading ? (
                  <CircularProgress size={20} style={{ color: "white" }} />
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
            <CommonFooter />
            {LogoutModal}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <button id="agency-verify-phone-submit-btn" style={{ display: "none" }}>
        Send Otp
      </button>

      <Dialog
        open={agentRegistrationPopup.open}
        maxWidth={"sm"}
        fullWidth={true}
        disablePortal={true}
      >
        <DialogTitle>{"Agent registration is pending!"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your agent profile registration is pending, please complete your
            agent profile now
          </DialogContentText>
        </DialogContent>
        <DialogActions className="confirmation-modal-buttons">
          <button
            className="button-sm-primary"
            onClick={confirmAgentRegistration}
          >
            Complete Agent Registration
          </button>
        </DialogActions>
      </Dialog>

      <div className="steps-container">
        <AgentCreatingAgencySteps
          step={CommonEnums.agencyProfileSteps.verifyPhone}
          agencyData={state.agencyData}
        />
      </div>

      <div className="agency-verify-phone-container verify-view">
        <div className="content-wrapper">
          <h2>Verify Phone</h2>
          <p className="sub-text">
            We sent an 6-digit verification code on provided mobile number
            <br />
            Please check your phone for verification code
          </p>

          <div className="field-header-row">
            <span className="label">Work Mobile*</span>
            <span className="edit-action" onClick={onClickEdit}>
              Edit
            </span>
          </div>

          <div className="mobile-input-row">
            <Select
              value={state?.phoneCode}
              onChange={updatePhoneCode}
              className="country-select"
              variant="standard"
              disableUnderline
              MenuProps={{ disablePortal: true }}
              disabled
            >
              <MenuItem value="+61">+61</MenuItem>
              <MenuItem value="+91">+91</MenuItem>
              <MenuItem value="+64">+64</MenuItem>
              <MenuItem value="+1">+1</MenuItem>
              <MenuItem value="+44">+44</MenuItem>
            </Select>
            <TextField
              variant="standard"
              placeholder="1234567890"
              value={state?.phoneNo}
              onChange={(e) =>
                setState((prev: any) => ({
                  ...prev,
                  phoneNo: e?.target?.value,
                }))
              }
              className="number-input"
              InputProps={{ disableUnderline: true }}
              disabled
            />
          </div>

          <div className="field-header-row mt-20">
            <span className="label">Verification Code*</span>
          </div>

          <div className="input-box-wrapper">
            <TextField
              variant="standard"
              placeholder="Enter Verification Code"
              className="grey-input-box"
              id="verify-phone-input"
              value={state.verificationCode}
              onChange={handleCodeChange}
              error={state.verificationCodeInputError}
              fullWidth
              InputProps={{ disableUnderline: true }}
            />
          </div>

          <p className="resend-text">
            Didn't receive the code?
            <span onClick={onClickResendOtp} className="resend-link">
              {state.isLoadingResend ? "Resending..." : "Resend"}
            </span>
          </p>

          <div className="button-row">
            <button className="btn-back-outline" onClick={handleBack}>
              Back
            </button>
            <button
              className="btn-filled"
              onClick={verifyOtp}
              disabled={!state.verificationCode}
            >
              {state?.isLoading ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                "Verify & continue"
              )}
            </button>
          </div>

          <CommonFooter />
          {LogoutModal}
        </div>
      </div>
    </>
  );
};

export default AgentAgencyProfileVerifyPhone;
